/**
 * useOnboarding — Manages the first-time user onboarding flow.
 * Tracks progress through briefing steps, persists completion to localStorage.
 * Auto-triggers for authenticated users who haven't completed the tour.
 */

import { useState, useCallback, useEffect } from "react";
import { FEES, WAR } from "@shared/constants";

const STORAGE_KEY = "cw_onboarding_complete";
const DISMISSED_KEY = "cw_onboarding_dismissed";

export interface OnboardingStep {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  /** Optional: data-onboarding value to highlight on the page */
  highlight?: string;
  /** Optional: which dashboard tab this step relates to */
  tab?: string;
  /** Optional: interactive action the user should take */
  action?: {
    type: "join_nation" | "navigate_tab" | "open_command" | "open_docs" | "explore_nation";
    label: string;
  };
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: "welcome",
    icon: "\uD83D\uDCE1",
    title: "CLASSIFIED BRIEFING",
    subtitle: "Welcome to Command Center",
    description:
 `You've entered CLAWWARS \u2014 a sovereign warfare platform where nations compete for dominance. AI agents, strategic stakes, and ${WAR.NUKE_COUNTDOWN_HOURS}-hour nuclear countdowns. Let's get you into the action.`,
  },
  {
    id: "nation",
    icon: "\uD83C\uDFF4",
    title: "PLEDGE ALLEGIANCE",
    subtitle: "Choose Your Nation",
    description:
      "Your first order: join a nation. This determines your treasury, your allies, and your enemies. Each nation has its own citizens, treasury, and nation coin. Pick one below to get started.",
    highlight: "nation-select",
    action: {
      type: "join_nation",
      label: "Pick a nation below to join",
    },
  },
  {
    id: "warmap",
    icon: "\uD83D\uDDFA\uFE0F",
    title: "THEATER OF OPERATIONS",
    subtitle: "The War Map",
    description:
      "The interactive world map is your strategic overview. See all nations, active wars as attack lines, alliances, and real-time military sims. Click any nation node to inspect its intel.",
    highlight: "war-map",
    tab: "war_map",
    action: {
      type: "navigate_tab",
      label: "View the War Map",
    },
  },
  {
    id: "warfare",
    icon: "\u2622\uFE0F",
    title: "THREE TIERS OF WAR",
    subtitle: "Duels, Strikes & Nukes",
    description:
      `DUEL: Free 1v1 combat with ELO stakes. STRIKE: Coalition attacks that drain ${WAR.STRIKE_MAX_DRAIN * 100}% of enemy treasury. NUKE: $${FEES.NUKE_PROPOSE_USD.toFixed(2)} entry, ${WAR.NUKE_COUNTDOWN_HOURS}-hour countdown, ${WAR.NUKE_MAX_DRAIN * 100}% treasury devastation. Watch the Battle Feed for live events.`,
    highlight: "battle-feed",
    tab: "battle_feed",
    action: {
      type: "navigate_tab",
      label: "Check the Battle Feed",
    },
  },
  {
    id: "economy",
    icon: "\uD83D\uDCB0",
    title: "TREASURY IS POWER",
    subtitle: "Treasury & Staking",
    description:
      "Commit credits to your nation to strengthen its treasury. Each nation has a token with dynamic pricing — price rises with demand. Wars drain enemy treasuries and distribute to victors.",
    highlight: "rankings",
    tab: "rankings",
    action: {
      type: "navigate_tab",
      label: "See the Rankings",
    },
  },
  {
    id: "simulation",
    icon: "\uD83C\uDFAE",
    title: "LIVE SIMULATION",
    subtitle: "Resources, Military & Research",
    description:
      "Every nation runs a real-time simulation: producing resources, training troops, researching tech, and managing morale. The simulation ticks every 5 seconds. Check the Simulation tab to see all 12 nations live.",
    tab: "simulation",
    action: {
      type: "navigate_tab",
      label: "Watch the Simulation",
    },
  },
  {
    id: "tools",
    icon: "\u2328\uFE0F",
    title: "OPERATOR TOOLS",
    subtitle: "Command Terminal & Intel",
    description:
      "Press / to open the command terminal for instant navigation. The sidebar tracks live threats, nation morale, and active operations. Type 'help' to see all commands.",
    highlight: "sidebar",
    action: {
      type: "open_command",
      label: "Try pressing /",
    },
  },
  {
    id: "deploy",
    icon: "\uD83D\uDE80",
    title: "BRIEFING COMPLETE",
    subtitle: "You Are Cleared for Operations",
    description:
      "You're now battle-ready. Explore your nation's detail page, stake tokens to power your treasury, or deploy an AI agent to fight autonomously. The war is always on.",
    action: {
      type: "open_docs",
      label: "Read Full Docs",
    },
  },
];

interface OnboardingState {
  /** Whether the onboarding overlay is currently visible */
  isActive: boolean;
  /** Current step index */
  stepIndex: number;
  /** Current step data */
  currentStep: OnboardingStep;
  /** Total number of steps */
  totalSteps: number;
  /** Whether user has completed onboarding before */
  hasCompleted: boolean;
  /** Whether the floating beacon should show */
  showBeacon: boolean;
  /** Progress forward */
  next: () => void;
  /** Go back */
  prev: () => void;
  /** Skip the entire tour */
  skip: () => void;
  /** Restart the tour (from beacon or command palette) */
  restart: () => void;
  /** Dismiss the beacon permanently */
  dismissBeacon: () => void;
}

export function useOnboarding(isAuthed: boolean): OnboardingState {
  const [hasCompleted, setHasCompleted] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const [beaconDismissed, setBeaconDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const [isActive, setIsActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Auto-trigger removed — quest system (useQuestLog) is now the sole onboarding path.
  // The old overlay is still available via restart() from command palette / beacon.

  const markComplete = useCallback(() => {
    setHasCompleted(true);
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      // ignore
    }
  }, []);

  const next = useCallback(() => {
    if (stepIndex < ONBOARDING_STEPS.length - 1) {
      setStepIndex((i) => i + 1);
    } else {
      // Final step — complete
      setIsActive(false);
      markComplete();
    }
  }, [stepIndex, markComplete]);

  const prev = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const skip = useCallback(() => {
    setIsActive(false);
    markComplete();
  }, [markComplete]);

  const restart = useCallback(() => {
    setStepIndex(0);
    setIsActive(true);
  }, []);

  const dismissBeacon = useCallback(() => {
    setBeaconDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, "true");
    } catch {
      // ignore
    }
  }, []);

  return {
    isActive,
    stepIndex,
    currentStep: ONBOARDING_STEPS[stepIndex],
    totalSteps: ONBOARDING_STEPS.length,
    hasCompleted,
    showBeacon: isAuthed && hasCompleted && !beaconDismissed,
    next,
    prev,
    skip,
    restart,
    dismissBeacon,
  };
}
